import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";
import { caseDisplayName } from "@/lib/caseName";
import { photoUrl } from "@/pages/CaseWorkspace";
import {
  FolderOpen, FileText, NotebookPen, MessagesSquare, Clock, ArrowRight,
  Play, Check, ChevronDown, Scale, Search, Dna, Trophy, HeartHandshake, CalendarClock, Users, Youtube, GraduationCap,
} from "lucide-react";

const BENEFITS = [
  { icon: FolderOpen, title: "Organized Case Workspaces", desc: "Everything for a case in one place — overview, timeline, documents, video, and discussion — instead of scattered comment threads." },
  { icon: FileText, title: "Source Documents & Evidence", desc: "Where available, the actual filings, police records, and press releases — linked and organized so you can read the primary sources yourself." },
  { icon: NotebookPen, title: "Detective Notes & Analysis", desc: "Christine Burke's investigative notes on what matters, what to watch, and how the process actually works." },
  { icon: MessagesSquare, title: "Member Discussions & Live Reviews", desc: "Informed discussion boards and eligible member-only live reviews — a real conversation, not a comment section." },
];

const STEPS = [
  { n: "01", icon: Search, title: "Choose a case", desc: "Pick a case you're following from Christine's coverage." },
  { n: "02", icon: FileText, title: "Review the organized evidence", desc: "Open the timeline, read the source documents, and study Christine's detective notes." },
  { n: "03", icon: MessagesSquare, title: "Join the ongoing case analysis", desc: "Weigh in on member discussions and eligible live reviews as the case develops." },
];

const KB_TIMELINE = [
  { date: "Nov 13, 2022", title: "Four University of Idaho students found dead in Moscow, Idaho" },
  { date: "Dec 30, 2022", title: "Bryan Kohberger arrested in Pennsylvania" },
  { date: "Jan 5, 2023", title: "Extradited to Idaho; probable-cause affidavit unsealed" },
  { date: "May 22, 2023", title: "Grand jury indictment returned" },
  { date: "Jul 2, 2025", title: "Guilty plea entered" },
  { date: "Jul 23, 2025", title: "Sentenced to life without parole" },
];

const KB_DOCS = [
  { t: "City of Moscow — Kohberger Investigation Documents", k: "Police Report" },
  { t: "Idaho State Police — Moscow Homicide Records Released", k: "Press Release" },
  { t: "Idaho State Police — Moscow Homicide Records (reports)", k: "Police Report" },
];

const DISCUSSION = [
  { t: "Understanding the charges", b: "Court Documents" },
  { t: "Tracking the civil lawsuit timeline", b: "New Developments" },
  { t: "Question about accident reconstruction basics", b: "Detective Questions" },
];

const FAQ = [
  { q: "Is this an official police investigation?", a: "No. Help Solve the Case is an educational true-crime community. Members review public information and organized case materials — no one here acts as an official investigator, and nothing on the site is an official law-enforcement investigation." },
  { q: "Can members view actual court records and source documents?", a: "Yes. Where they are publicly available, cases include links to court filings, police records, press releases, and other source documents — gathered and organized in one place so you can read the primary sources yourself." },
  { q: "What types of cases are included?", a: "Active and in-court cases, cold cases, missing persons, unidentified remains, wrongful-conviction claims, and DNA / genetic-genealogy cases. New cases and materials are added over time." },
  { q: "Is this the same material available on YouTube?", a: "No. YouTube is where Christine covers a case on video. Membership is where you open the organized case file — documents, timeline, detective notes, and discussion — to keep reviewing the evidence after the video ends." },
  { q: "Can I cancel?", a: "Yes. You can cancel anytime from your dashboard. Plans renew automatically until canceled, and there's no long-term contract." },
  { q: "Does membership include live discussions?", a: "Membership includes eligible member-only live reviews and discussions with Christine. New sessions and materials are added over time." },
];

function BrowserFrame({ label, children }) {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0f0f10] shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="ml-3 label-mono text-white/40 truncate">{label}</span>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function Landing() {
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    api.get("/public/featured-case").then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);
  const [openFaq, setOpenFaq] = useState(0);

  const fTitle = featured?.title || "";
  const fName = fTitle.replace(/^The\s+/i, "").replace(/\s+Case\b.*$/i, "").trim() || "the case";
  const fImg = photoUrl(featured?.primary_photo) || featured?.thumbnail;

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/7319068/pexels-photo-7319068.jpeg" alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-charcoal/92" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-9 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 label-mono mb-5 px-3 py-1.5 border border-gold/40 rounded-sm bg-charcoal/85 text-gold shadow-lg" data-testid="hero-badge" style={{ color: "#C6A15B" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /> Retired detective. Real cases. Work them with me.
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
                Follow the Evidence.<br /><span className="text-gold">Think Like a Detective.</span>
              </h1>
              <p className="mt-4 sm:mt-5 text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl font-medium">
                Watch the case. Work the case. Go beyond the headlines.
              </p>
              <p className="mt-3 sm:mt-4 text-base text-white/60 leading-relaxed max-w-xl">
                Explore real criminal cases, follow the evidence, and join a community built around
                investigative thinking.
              </p>
              <div className="mt-6 sm:mt-9 flex flex-wrap items-center gap-3">
                <Link to="/register" data-testid="hero-primary-cta" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
                  Join Free — No Credit Card Required <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/cases" data-testid="hero-secondary-cta" className="px-6 py-3.5 border border-white/15 text-white rounded-sm hover:border-gold/40 transition-colors">
                  Explore the Cases
                </Link>
              </div>
              <p className="mt-5 text-sm text-white/70 font-mono">
                Free to join · Upgrade anytime for <span className="text-gold font-semibold">$4.99/mo</span> · <Link to="/login" className="hover:text-gold underline">Member login</Link>
              </p>
            </motion.div>

            {/* Hero visual — real case-file interface */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="hidden lg:block">
              <BrowserFrame label="helpsolvethecase.com / case file">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-mono text-gold mb-1">Case Workspace</div>
                    <div className="font-display text-xl font-semibold text-white">The Case File</div>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-1 rounded-sm border border-gold/40 bg-gold/10 text-gold">ACTIVE</span>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {["Overview", "Timeline", "Documents", "Notes"].map((t, i) => (
                    <div key={t} className={`text-center text-[11px] font-mono py-2 rounded-sm border ${i === 1 ? "border-gold/40 bg-gold/10 text-gold" : "border-white/10 text-white/45"}`}>{t}</div>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {KB_TIMELINE.slice(0, 4).map((e) => (
                    <div key={e.date} className="flex gap-3 items-start border-b border-white/5 pb-2.5 last:border-0">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                      <div>
                        <div className="font-mono text-[11px] text-gold">{e.date}</div>
                        <div className="text-sm text-white/75 leading-snug">{e.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </BrowserFrame>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRODUCING THE COURSE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20" data-testid="course-feature">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-lg border border-gold/30 card-surface grain">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
            <div className="p-8 sm:p-12">
              <div className="inline-flex items-center gap-2 label-mono mb-4 px-3 py-1.5 border border-gold/40 rounded-sm bg-gold/10 text-gold">
                <GraduationCap className="w-4 h-4" /> New · 12-week program
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
                Introducing the <span className="text-gold">Homicide Investigations Course</span>
              </h2>
              <p className="mt-4 text-white/65 leading-relaxed max-w-xl">
                Learn to work a death investigation the way a real detective does — decision by decision,
                from the first call to the courtroom. Twelve weekly lessons in Christine&apos;s own voice,
                ending in your Certified Detective badge. Included with membership.
              </p>
              <p className="mt-5 text-sm text-gold font-mono flex items-center gap-2">
                <CalendarClock className="w-4 h-4" /> Open to members now · New lesson every week
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/course" data-testid="course-explore-cta" className="inline-flex items-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
                  Explore the course <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" data-testid="course-feature-join" className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/15 text-white font-semibold rounded-sm hover:border-gold/50 transition-colors">
                  Join as a member
                </Link>
              </div>
            </div>
            <div className="relative min-h-[220px] lg:min-h-full border-t lg:border-t-0 lg:border-l border-gold/15">
              <img src="/course-bg.jpg" alt="Homicide Investigations Course" className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-l from-charcoal via-charcoal/40 to-transparent" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* THE DIFFERENCE — YOUTUBE vs BULLPEN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20">
        <div className="label-mono mb-3 text-gold">The difference</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-3xl leading-tight">
          YouTube is where I show you what I found.
          <span className="text-gold block mt-1">The Bullpen is where we work it out together.</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mt-10 items-stretch">
          <div className="card-surface rounded-md p-7 border border-white/10 flex flex-col">
            <div className="flex items-center gap-2.5 mb-1">
              <Youtube className="w-6 h-6 text-gold" />
              <span className="font-display text-2xl font-bold text-white">On YouTube</span>
            </div>
            <p className="text-white/50 text-sm mb-5">Free, public — where I show you what I found.</p>
            <div className="grid gap-2.5">
              {["Case breakdowns & investigation analysis", "Court hearing reviews", "Pleading & motion reviews", "Case precedents & key rulings"].map((t) => (
                <div key={t} className="flex items-start gap-2 text-sm text-white/85"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {t}</div>
              ))}
            </div>
            <a href="https://www.youtube.com/@helpsolvethecase?sub_confirmation=1" target="_blank" rel="noopener noreferrer" data-testid="watch-youtube-cta"
              className="mt-auto pt-6 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-white font-semibold rounded-sm hover:border-gold/50 transition-colors text-sm self-start">
              <Youtube className="w-4 h-4 text-gold" /> Subscribe on YouTube <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="card-surface rounded-md p-7 border border-gold/25 bg-gold/[0.04] flex flex-col">
            <div className="flex items-center gap-2.5 mb-1">
              <Users className="w-6 h-6 text-gold" />
              <span className="font-display text-2xl font-bold text-gold">Inside Help Solve the Case</span>
            </div>
            <p className="text-white/50 text-sm mb-5">Members work it with me — our private case room, the Bullpen.</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {["Bring me your questions directly", "Challenge the evidence", "Pull apart search warrants", "Examine police policies & procedures", "Test prosecution & defense theories", "Follow the documents & timelines", "Participate in live case reviews", "Help decide what we investigate next"].map((t) => (
                <div key={t} className="flex items-start gap-2 text-sm text-white/85"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {t}</div>
              ))}
            </div>
            <Link to="/pricing" data-testid="bullpen-seat-cta"
              className="mt-auto pt-6 inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm self-start">
              Get a seat in the Bullpen — $4.99/mo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section id="what-you-get" className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 scroll-mt-20">
        <div className="label-mono mb-3 text-gold">What you get</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl leading-tight">
          The case file, organized — with a detective&apos;s eye on the evidence.
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="card-surface rounded-md p-6 flex gap-4">
              <div className="shrink-0 w-11 h-11 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center">
                <b.icon className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <div className="label-mono mb-3 text-gold">How it works</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white mb-10">Three steps to keep working the case.</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card-surface rounded-md p-7">
              <div className="flex items-center justify-between mb-5">
                <span className="font-display text-3xl font-bold text-white/15">{s.n}</span>
                <s.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <div className="label-mono mb-3 text-gold">A look inside</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white mb-10 max-w-2xl">This is what opens when the video ends.</h2>
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Timeline */}
          <BrowserFrame label="Case File · Timeline">
            <div className="label-mono text-gold mb-4">Evidence timeline</div>
            <div className="space-y-3">
              {KB_TIMELINE.map((e) => (
                <div key={e.date} className="flex gap-3 items-start border-b border-white/5 pb-3 last:border-0">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[11px] text-gold">{e.date}</div>
                    <div className="text-sm text-white/75">{e.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </BrowserFrame>
          {/* Documents */}
          <BrowserFrame label="Case File · Documents">
            <div className="label-mono text-gold mb-4">Source-document library</div>
            <div className="space-y-3">
              {KB_DOCS.map((d) => (
                <div key={d.t} className="flex items-start gap-3 border border-white/8 rounded-sm p-3 bg-white/[0.02]">
                  <FileText className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white/80 truncate">{d.t}</div>
                    <span className="mt-1 inline-block text-[10px] font-mono px-2 py-0.5 rounded-sm border border-white/10 text-white/45">{d.k}</span>
                  </div>
                </div>
              ))}
            </div>
          </BrowserFrame>
          {/* Detective note */}
          <BrowserFrame label="Case File · Detective Notes">
            <div className="label-mono text-gold mb-4">Detective note</div>
            <div className="card-surface rounded-sm p-4">
              <div className="font-display text-base font-semibold text-white mb-2">How to study this case</div>
              <p className="text-sm text-white/60 leading-relaxed">
                This case is an excellent study in investigative genetic genealogy (IGG). Focus on the science and
                procedure: how IGG narrows a candidate pool from crime-scene DNA, and why it is an investigative lead
                rather than courtroom proof…
              </p>
              <div className="mt-4 flex items-center gap-2 label-mono text-white/45">
                <NotebookPen className="w-3.5 h-3.5 text-gold" /> Christine Burke · FGG®
              </div>
            </div>
          </BrowserFrame>
          {/* Discussion */}
          <BrowserFrame label="Case File · Community">
            <div className="label-mono text-gold mb-4">Member discussion</div>
            <div className="space-y-3">
              {DISCUSSION.map((d) => (
                <div key={d.t} className="flex items-start gap-3 border border-white/8 rounded-sm p-3 bg-white/[0.02]">
                  <MessagesSquare className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm text-white/80">{d.t}</div>
                    <span className="mt-1 inline-block text-[10px] font-mono px-2 py-0.5 rounded-sm border border-gold/25 bg-gold/5 text-gold/80">{d.b}</span>
                  </div>
                </div>
              ))}
            </div>
          </BrowserFrame>
        </div>
      </section>

      {/* FEATURED CASE */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
          <div className="label-mono mb-3 text-gold">Featured case</div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="grid lg:grid-cols-2 gap-0 rounded-lg overflow-hidden border border-white/10 card-surface">
            <div className="relative min-h-[240px] lg:min-h-full">
              {fImg ? <img src={fImg} alt={fTitle} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-white/5" />}
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent" />
            </div>
            <div className="p-8 sm:p-10">
              {featured.status && <span className="inline-block text-[10px] font-mono px-2.5 py-1 rounded-sm border border-gold/40 bg-gold/10 text-gold mb-4">{featured.status}</span>}
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight">{caseDisplayName(fTitle)}</h3>
              {featured.location && <div className="mt-2 label-mono text-white/45">{featured.location}</div>}
              {featured.summary && <p className="mt-4 text-sm text-white/60 leading-relaxed line-clamp-4">{featured.summary}</p>}
              <Link to={`/cases/${featured.slug}`} data-testid="featured-case-cta"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
                Open the {fName} Case File <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </section>
      )}

      {/* WHY CHRISTINE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-12 items-center">
          <div className="mx-auto lg:mx-0 w-56 h-56 rounded-md overflow-hidden border border-gold/25 bg-white">
            <img src="/christine-burke.webp" alt="Detective Sgt. Christine Burke, FGG®" className="w-full h-full object-cover object-top" data-testid="christine-photo" />
          </div>
          <div>
            <div className="label-mono mb-3 text-gold">Why Christine</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-tight">
              Work the case with a real investigator.
            </h2>
            <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">
              Christine Burke isn&apos;t a true-crime commentator. She&apos;s a retired police detective and sergeant,
              private investigator, forensic genetic genealogist, and law-enforcement expert witness with 25+ years in
              investigations. Professional clients pay hundreds of dollars an hour for her analysis — membership puts
              that experience to work alongside you, on real cases.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-2xl">
              {["Retired Police Detective & Sergeant", "Private Investigator", "Forensic Genetic Genealogist", "Law-Enforcement Expert Witness", "25+ Years in Investigations"].map((c) => (
                <div key={c} className="flex items-center gap-2.5 text-sm text-white/75">
                  <Check className="w-4 h-4 text-gold shrink-0" /> {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FREE VS MEMBER COMPARISON */}
      <section id="free-vs-member" className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 scroll-mt-20" data-testid="compare-section">
        <div className="max-w-2xl mb-10">
          <div className="label-mono mb-3 text-gold">Start free. Upgrade when you're ready.</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Free registration is your starting point — not a trial that expires.</h2>
          <p className="mt-4 text-white/60">Create a free account in seconds and start following a case today. Become a Founding Member whenever you want the full toolkit.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="card-surface rounded-md p-7 border border-white/10 flex flex-col" data-testid="tier-free">
            <div className="label-mono text-white/60">Free account</div>
            <div className="mt-2 font-display text-2xl font-bold text-white">$0 · No credit card</div>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              {["Browse the full case directory",
                "Follow one case at a time",
                "Case overview + basic timeline",
                "Read community discussion",
                "Post a limited number of comments"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
            <Link to="/register" data-testid="compare-free-cta" className="mt-auto pt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold font-semibold rounded-sm hover:bg-gold/10 transition-colors">
              Join Free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="card-surface rounded-md p-7 border border-gold/40 relative flex flex-col" data-testid="tier-member">
            <span className="absolute top-4 right-4 text-[10px] font-mono font-bold uppercase tracking-wider text-charcoal bg-gold px-2.5 py-1 rounded-sm shadow">Most popular</span>
            <div className="label-mono text-gold">Founding Member</div>
            <div className="mt-2 font-display text-2xl font-bold text-white">$4.99/mo <span className="text-base text-white/50 font-normal">or $49.99/yr</span></div>
            <ul className="mt-5 space-y-3 text-sm text-white/80">
              {["Every eligible member case workspace",
                "Full documents & case materials",
                "Evidence Boards & Detective Notes",
                "Member discussions + full posting",
                "AI investigative assistance",
                "Member Bullpen access & recordings"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
            <Link to="/pricing" data-testid="compare-member-cta" className="mt-auto pt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
              Become a Founding Member <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <p className="mt-5 text-xs text-white/40 font-mono">The Detective Academy and Defense League are separate premium experiences and are not included with Founding Membership.</p>
      </section>

      {/* MISSION */}
      <section id="mission" className="max-w-4xl mx-auto px-4 sm:px-6 pt-24" data-testid="mission-section">
        <div className="card-surface grain rounded-lg border border-white/10 p-8 sm:p-12">
          <div className="label-mono mb-3 text-gold">Our mission</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Building something bigger than a case community.</h2>
          <p className="mt-4 text-white/70 leading-relaxed">
            Help Solve The Case is an educational community for people who think like investigators. Our long-term
            goal is to build sustainable funding to support <span className="text-white">pro bono investigative work and
            forensic genetic genealogy</span> aimed at identifying unidentified human remains across the United States —
            and helping bring answers to families who have waited far too long.
          </p>
          <p className="mt-4 text-xs text-white/40 font-mono">
            This is our mission and direction. It does not imply that subscription revenue is currently being donated
            or that investigations are already funded through this initiative.
          </p>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <section id="membership" className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
        <div className="rounded-lg border border-white/10 card-surface grain p-8 sm:p-12">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <div className="label-mono mb-3 text-gold">Membership</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white leading-tight">
                One membership. Get in the case room.
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                Work real cases with Christine: organized case files, source documents and timelines, her detective
                notes, live Bullpen case reviews, and the growing Case Review Library of past Bullpens. Bring your
                questions, challenge the evidence, and help decide what we look at next.
              </p>
              <div className="mt-6 space-y-2.5">
                {["Cancel anytime", "Plans renew automatically until canceled", "No long-term contract", "Immediate access after payment"].map((t) => (
                  <div key={t} className="flex items-center gap-2.5 text-sm text-white/75"><Check className="w-4 h-4 text-gold shrink-0" /> {t}</div>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-md border border-white/10 p-6 bg-charcoal">
                <div className="label-mono text-white/45">Monthly</div>
                <div className="mt-2 font-display text-3xl font-bold text-white">$4.99<span className="text-base font-normal text-white/40">/mo</span></div>
                <div className="mt-1 text-xs text-white/40 font-mono">Billed monthly · auto-renews</div>
              </div>
              <div className="rounded-md border border-gold/40 p-6 bg-gold/[0.06] relative">
                <span className="absolute -top-2.5 left-6 text-[10px] font-mono px-2 py-0.5 rounded-sm bg-gold text-charcoal">BEST VALUE</span>
                <div className="label-mono text-gold">Annual</div>
                <div className="mt-2 font-display text-3xl font-bold text-white">$49.99<span className="text-base font-normal text-white/40">/yr</span></div>
                <div className="mt-1 text-xs text-gold/80 font-mono">Save $9.89 — about 2 months free</div>
              </div>
              <div className="sm:col-span-2">
                <Link to="/pricing" data-testid="membership-cta" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
                  Work the case with me <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-24">
        <div className="label-mono mb-3 text-gold text-center">FAQ</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white text-center mb-10">Questions, answered.</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <div key={f.q} className="card-surface rounded-md overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-${i}`}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-white/[0.02] transition-colors">
                <span className="font-medium text-white text-sm sm:text-base">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-gold shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <div className="px-5 pb-5 text-sm text-white/60 leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* SECONDARY — mission / DNA / ranks (moved lower, kept) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/move-your-dna" data-testid="secondary-dna" className="card-surface rounded-md p-6 hover:border-gold/30 transition-colors block">
            <Dna className="w-6 h-6 text-gold mb-4" />
            <h3 className="font-display text-base font-semibold text-white">Move Your DNA</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">Help identify unknown remains by uploading your DNA to GEDmatch & FamilyTreeDNA. We&apos;ll walk you through it.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs text-gold font-semibold">Learn how <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
          <div className="card-surface rounded-md p-6">
            <HeartHandshake className="w-6 h-6 text-gold mb-4" />
            <h3 className="font-display text-base font-semibold text-white">Enduring Service™</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">Your membership helps fund investigative support for underfunded police agencies working to give families answers.</p>
          </div>
          <div className="card-surface rounded-md p-6">
            <Trophy className="w-6 h-6 text-gold mb-4" />
            <h3 className="font-display text-base font-semibold text-white">Earn your rank</h3>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">Contribute to discussions and climb the members-only leaderboard, from Case Follower to Lead Detective.</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="card-surface rounded-lg p-10 sm:p-16 text-center grain">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">You&apos;ve watched the case. Now come work it.</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">Get in the Bullpen, bring your questions to Christine, and unlock every case review we&apos;ve already done — for $4.99/month. Cancel anytime.</p>
          <Link to="/pricing" data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
            Work the case with me <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
